<template>
	<div class="VideoPlayer" :class="classes">
		<div class="VideoPlayer-container" dir="ltr" @keyup.space.capture.prevent="togglePlay" tabindex="0">
			<div class="VideoPlayer-video" @click.prevent="togglePlay">
				<video
					:src="media"
					:poster="poster"
					ref="video"
					:id="videoId"
					controlslist="nodownload"
					@timeupdate="timeUpdated"
					@loadedmetadata="loadedMetadata"
					@playing="isPlaying"
					@waiting="isWaiting"
					@canplay="canPlay"
					@pause="isPaused"
					@contextmenu.prevent
					:preload="preload"
					:loop="map"
				></video>
			</div>
			<!--media controls begins from here-->
			<CloMediaControls
				:duration="duration"
				:currentTime="time"
				:progressPercent="progressPercent"
				:seekContainerClasses="map ? 'MediaControls-seek-container--reverse' : ''"
				:controlsClasses="map ? 'MediaControls-controls--reverse' : ''"
				:playing="playing"
				:messages="messages"
				:showVolume="showVolume"
				:citationUrl="citationUrl"
				:specimenUrl="specimenUrl"
				:hdAvailable="hdAvailable"
				:hdActive="hdActive"
				:showFullscreen="showFullscreen"
				:hideBranding="hideBranding"
				:hideTime="hideTime"
				:hideVolume="hideVolume"
				:frameStep="frameStep"
				v-on:togglePlay="togglePlay"
				:assetId="'33842721'"
				:animatedText="animatedText"
				v-on:setVolume="setVolume"
				v-on:toggleVolume="toggleVolume"
				v-on:setTime="setTime"
				v-on:setPosition="setPosition"
				v-on:toggleHd="toggleHd"
				v-on:toggleFullscreen="toggleFullscreen"
				v-on:previousFrame="previousFrame"
				v-on:nextFrame="nextFrame"
				ref="controls"
			></CloMediaControls>

			<div v-if="showLoading" class="LoadingAnimation">
				<CloIcon symbol="spinner" />
			</div>
			<div v-if="bigPlayButton && !this.startFrame" class="VideoPlayer-bigPlayButton">
				<button class="Button u-margin-none" type="button" :aria-label="messages.play" @click.prevent.capture="togglePlay">
					<CloIcon symbol="play" />
				</button>
			</div>
		</div>
	</div>
</template>

<script>
import axios from 'axios';
import CloButton from '@/base/CloButton/CloButton';
import CloLoadingAnimation from '@/base/CloLoadingAnimation/CloLoadingAnimation';
import CloMediaControls from '@/media/CloMediaControls/CloMediaControls';
import CloIcon from '@/icons/CloIcon/CloIcon.vue';

export default {
	components: { CloButton, CloLoadingAnimation, CloMediaControls, CloIcon },

	data() {
		return {
			playing: false,
			isActive: false,
			showLoading: true,
			progressPercent: 0.0,
			time: 0,
			duration: 0,
			durationText: '',
			switchTime: 0,
			switchingSource: false,
			volumeSlider: 1,
			showVolume: false,
			fullscreen: false,
			hdActive: false,
			hdAvailable: false,
			disableResize: false,
			showFullscreen: true,
			hideVolume: false,
			hideBranding: false,
			hideVolume: false,
			hideTime: false,
			disableFit: false,
			preload: 'metadata',
			bigPlayButton: false,
			notYetPlayed: true,
			specimenBaseUrl: 'https://macaulaylibrary.org/asset/',
			startFrame: null,
			classes: '',
			showVideo: false,
		};
	},

	props: {
		/**
		 * The ID of the media provided from the database
		 */
		assetId: {}, // can be Number or String
		citationUrl: {
			default: ' ',
		},
		poster: String,
		/**
		 * To fit the component to the provided height or not
		 */
		fitToHeight: {
			default: false,
			type: Boolean,
		},
		/**
		 * Maximum height for the component
		 */
		maxHeight: {
			type: Number,
		},
		/**
		 * Maximum width for the component
		 */
		maxWidth: {
			type: Number,
		},
		/**
		 * This is the HD feature
		 */
		hd: {
			default: false,
		},
		/**
		 * Indicates whether to play media automatically or not
		 */
		autoplay: {
			default: false,
			type: Boolean,
		},
		disableMLLink: {
			default: false,
			type: Boolean,
		},
		map: {
			default: false,
			type: Boolean,
		},
		mapstem: {
			default: false,
			type: Boolean,
		},
		frameStep: {}, // may be Number or String
		mediaDownloadUrl: {
			default: 'https://cdn.download.ams.birds.cornell.edu/api/v1/asset/',
			type: String,
		},
		animatedText: {
			type: String,
			default: '',
		},
	},
	computed: {
		media() {
			if (isNaN(this.assetId)) {
				this.mediaDownloadUrl = '';
			}
			if (this.map) {
				// use 1920x1920 for small and medium screen
				if (window.innerWidth < 1200) {
					return this.mediaDownloadUrl + this.assetId + '/mp4/1920'; // hd
				} else {
					return this.mediaDownloadUrl + this.assetId + '/mp4/3840'; // 4k
				}
			} else {
				if (this.hdActive) {
					return this.mediaDownloadUrl + this.assetId + '/mp4/1920'; // hd
				} else {
					return this.mediaDownloadUrl + this.assetId + '/mp4/1280';
				}
			}
		},
		specimenUrl() {
			// if citationUrl is false, the asset has no parent and specimenUrl is restricted
			// so link/branding should be hidden
			if (this.citationUrl === false) {
				return false;
			} else {
				return this.specimenBaseUrl + this.assetId;
			}
		},
		messages() {
			return {
				mute: 'mute',
				volume: 'volume',
				seek: 'seek',
				play: 'play',
				fullscreen: 'full screen',
				next: this.$t('generic.Next'),
				previous: this.$t('generic.Previous'),
			};
		},
		videoId() {
			return 'video-' + this._uid;
		},
	},
	beforeMount() {
		// hd can be "available", "default" (or null)
		this.hdAvailable = !!this.hd;
		this.hdActive = this.hd === 'default';

		if (this.hdAvailable) {
			let url = this.mediaDownloadUrl + this.assetId + '/mp4/1920';
			this.checkForHDFile(url);
		}

		if (this.map) {
			this.hideVolume = true;
			this.hideBranding = true;
			this.hideTime = true;
			this.showFullscreen = false;
			this.disableFit = true;
			this.bigPlayButton = true;
			this.preload = 'auto';
			this.hdAvailable = false;
			this.hdActive = false;
			const urlParams = new URLSearchParams(window.location.search);
			const frame = urlParams.get('frame');
			if (frame) {
				this.startFrame = frame;
			}
		}

		// mapstem (BNA) variant
		if (this.mapstem) {
			this.hideVolume = true;
			this.showFullscreen = false;
			this.hdAvailable = false;
			this.hdActive = false;
			this.disableMLLink = true;
		}
	},
	mounted() {
		document.addEventListener('lichen:resize', this.e);
		document.addEventListener('lichen:video-togglePlay', this.handleTogglePlayEvent);
		document.addEventListener('lichen:video-pause', this.pause);
		document.addEventListener('lichen:video-destroy', this.removeListeners);
	},
	beforeDestroy() {
		this.removeListeners();
	},
	methods: {
		removeListeners() {
			document.removeEventListener('lichen:resize', this.setSize);
			document.removeEventListener('lichen:video-togglePlay', this.handleTogglePlayEvent);
			document.removeEventListener('lichen:video-pause', this.pause);
			document.removeEventListener('lichen:video-destroy', this.removeListeners);
		},
		handleTogglePlayEvent(e) {
			if (e.detail.assetId === parseInt(this.assetId)) {
				this.togglePlay();
			}
		},
		togglePlay() {
			if (this.$refs.video) {
				if (this.playing) {
					this.$refs.video.pause();
				} else {
					this.$refs.video.play();
				}
			}
		},
		isPlaying() {
			this.playing = true;
			this.showLoading = false;
			this.bigPlayButton = false;
			this.notYetPlayed = false;
		},
		isPaused() {
			this.playing = false;
		},
		pause() {
			if (this.$refs.video) {
				this.$refs.video.pause();
			}
			this.playing = false;
		},
		// progress bar and timeline animation
		updateProgress() {
			this.progressPercent = (100 * this.time) / this.duration;
		},
		// time values should be formatted as 0:00
		formatTime(time) {
			return Math.floor(time / 60) + ':' + ('0' + Math.floor(time % 60)).slice(-2);
		},
		timeUpdated() {
			if (this.$refs.video) {
				this.time = this.$refs.video.currentTime;
				this.$emit('video-time-updated', this.time);
			}
			this.updateProgress();
		},
		setSize() {
			if (!this.disableResize && !this.disableFit) {
				let videoHeight;
				let videoWidth;
				let containerHeight;
				let containerWidth;
				if (this.fitToHeight) {
					// TODO: get rid of this option
					containerHeight = this.$el.parentNode.parentNode.parentNode.offsetHeight;
				} else {
					containerHeight = 9999;
				}
				if (this.maxHeight) {
					containerHeight = this.maxHeight;
				}
				if (this.maxWidth) {
					containerWidth = this.maxWidth;
				} else {
					// TODO: get rid of this option
					//containerWidth = this.$el.parentNode.parentNode.offsetWidth;
				}
				let containerAspectRatio = containerWidth / containerHeight;
				//let controlsHeight = this.$refs.controls.offsetHeight + 8; // add height of seek bar
				//let controlsHeight = Math.abs(this.$el.offsetHeight - this.originalHeight);
				let controlsHeight = 50;
				if (this.$refs.controls) {
					//controlsHeight = this.$refs.controls.offsetHeight;
				}
				let aspectRatioWithControls = this.originalWidth / (this.originalHeight + controlsHeight);

				if (this.originalHeight + controlsHeight > containerHeight) {
					// need to constrain height
					videoHeight = containerHeight - controlsHeight;
					// deal with width
					if (this.originalWidth <= containerWidth) {
						// enough width
						videoWidth = this.aspectRatio * videoHeight;
					} else {
						// too narrow
						videoWidth = containerWidth;
						if (containerAspectRatio < aspectRatioWithControls) {
							videoHeight = videoWidth / this.aspectRatio;
						} else {
							videoWidth = this.aspectRatio * videoHeight;
						}
					}
				} else {
					// expand up to original size
					if (containerHeight - controlsHeight >= this.originalHeight) {
						videoHeight = this.originalHeight;

						// deal with width
						if (this.originalWidth <= containerWidth) {
							// enough width
							videoWidth = this.aspectRatio * videoHeight;
						} else {
							// too narrow
							videoWidth = containerWidth;
							videoHeight = videoWidth / this.aspectRatio;
						}
					} else {
						videoHeight = containerHeight - controlsHeight;
					}
				}
				// minimums
				if (videoWidth < 320) {
					videoWidth = 320;
				}
				//console.log('new video (W x H)', videoWidth, videoHeight);
				if (this.$refs.video) {
					this.$refs.video.style.height = videoHeight + 'px';
					this.$refs.video.style.width = videoWidth + 'px';
				}
				//this.$refs.controls.style.width = videoWidth + 'px';
			}
			this.showVideo = true;
		},
		loadedMetadata(e) {
			//console.log('metadata loaded');
			this.showLoading = false;
			this.originalWidth = e.target.videoWidth;
			this.originalHeight = e.target.videoHeight;
			this.aspectRatio = this.originalWidth / this.originalHeight;

			// not sure why this delay helps
			if (!this.isActive) {
				this.isActive = true;
				setTimeout(() => {
					this.setSize();
				}, 50);
			}
			if (this.$refs.video) {
				const duration = this.$refs.video.duration;
				this.duration = duration;
				this.durationText = this.formatTime(duration);
			}
			// switching source
			if (this.switchingSource) {
				this.$refs.video.currentTime = this.switchTime;
				this.time = this.switchTime;
				this.updateProgress();
				if (this.switchingSource === 'continue') {
					this.$refs.video.play();
				}
				this.switchingSource = false;
			}

			// if url has ?frame=, jump to that frame (for animated map screenshots)
			if (this.startFrame) {
				// frame 1 = week 0
				this.$refs.video.currentTime = (this.startFrame - 1) / this.frameStep;
				// TODO: send event instead of setting class?
				this.classes = 'VideoPlayer--ready';
			}
		},
		// clicking on the "seek" slider, or adjusting the slider (end of drag)
		setTime(value) {
			requestAnimationFrame(() => {
				this.time = parseFloat(value);
				this.$refs.video.currentTime = this.time;
				this.updateProgress();
			});
		},
		// adjusting the seek slider (during drag)
		setPosition(value) {
			requestAnimationFrame(() => {
				this.time = parseFloat(value);
				this.updateProgress();
			});
		},
		setVolume(level) {
			this.$refs.video.volume = level;
			this.volumeToRestore = level;
		},
		toggleVolume() {
			if (this.volumeSlider == 0) {
				if (this.volumeToRestore == 0) {
					this.volumeSlider = 1;
				} else {
					this.volumeSlider = this.volumeToRestore;
				}
			} else {
				this.volumeSlider = 0;
			}
			this.setVolume();
		},
		toggleFullscreen() {
			const videoEl = this.$refs.video;
			const docEl = document.documentElement;
			if (docEl.requestFullscreen) {
				this.disableResize = true;
				document.addEventListener('fullscreenchange', this.fullscreenchange, false);
				videoEl.requestFullscreen();
			} else if (docEl.mozRequestFullScreen) {
				this.disableResize = true;
				document.addEventListener('mozfullscreenchange', this.fullscreenchange, false);
				videoEl.mozRequestFullScreen();
			} else if (docEl.webkitRequestFullScreen) {
				this.disableResize = true;
				document.addEventListener('webkitfullscreenchange', this.fullscreenchange, false);
				videoEl.webkitRequestFullScreen();
			} else if (docEl.msRequestFullscreen) {
				this.disableResize = true;
				document.addEventListener('MSFullscreenChange', this.fullscreenchange, false);
				videoEl.msRequestFullscreen();
			}
		},
		fullscreenchange(e) {
			// find which element is going full screen - IE needs activeElement, FF has wrong activeElement.
			// chrome, safari, and edge all correctly have target
			let targetId = typeof e.target.id !== 'undefined' ? e.target.id : e.target.activeElement.id;
			if (targetId === '') {
				// ff fix
				targetId = e.target.activeElement.querySelector('video').id;
			}
			//console.log(targetId, this.videoId);
			if (targetId === this.videoId) {
				// toggle browser controls UI for fullscreen
				if (
					document.fullscreenElement ||
					document.webkitCurrentFullScreenElement ||
					document.mozFullScreen ||
					document.msFullscreenElement
				) {
					// fullscreen
					this.$refs.video.setAttribute('controls', true);
					this.$refs.video.setAttribute('controlsList', 'nodownload');
				} else {
					const docEl = document.documentElement;
					//console.log('leave fullscreen');
					if (docEl.requestFullscreen) {
						document.removeEventListener('fullscreenchange', this.fullscreenchange, false);
					} else if (docEl.mozRequestFullScreen) {
						document.removeEventListener('mozfullscreenchange', this.fullscreenchange, false);
					} else if (docEl.webkitRequestFullScreen) {
						document.removeEventListener('webkitfullscreenchange', this.fullscreenchange, false);
					} else if (docEl.msRequestFullscreen) {
						document.removeEventListener('MSFullscreenChange', this.fullscreenchange, false);
					}
					setTimeout(() => {
						this.disableResize = false;
					}, 500);
					this.$refs.video.removeAttribute('controls');
				}
			}
		},
		toggleHd() {
			if (this.playing) {
				this.switchingSource = 'continue';
			} else {
				this.switchingSource = true;
			}
			this.hdActive = !this.hdActive;
			const videoEl = this.$refs.video;
			this.switchTime = videoEl.currentTime;
			if (this.hdActive) {
				videoEl.src = this.mediaDownloadUrl + this.assetId + '/mp4/1920';
			} else {
				videoEl.src = this.mediaDownloadUrl + this.assetId + '/mp4/1280';
			}
		},
		isWaiting() {
			//console.log('is waiting');
			this.showLoading = true;
		},
		canPlay() {
			//console.log('can play');
			this.showLoading = false;
			if (this.autoplay) {
				setTimeout(() => {
					//console.log(navigator.userAgent)
					// only chrome and ff can do this kind of autoplay
					if (navigator.userAgent.indexOf('Chrome') !== -1 || navigator.userAgent.indexOf('Firefox') !== -1) {
						//this.autoplay = false;
						if (this.$refs.video) {
							this.$refs.video.play();
						}
					}
				}, 350);
			}
		},
		previousFrame() {
			if (this.playing) {
				this.pause();
			}
			let time = (this.$refs.video.currentTime -= 1 / this.frameStep);
			if (time < 0) {
				time = this.duration - 0.01;
			}
			this.$refs.video.currentTime = time;
			if (this.notYetPlayed) {
				this.notYetPlayed = false;
				this.bigPlayButton = false;
				this.time = time;
				this.updateProgress();
			}
		},
		nextFrame() {
			if (this.playing) {
				this.pause();
			}

			let time = (this.$refs.video.currentTime += 1 / this.frameStep);
			if (time > this.duration) {
				time = 0.0;
			}
			this.$refs.video.currentTime = time;
			if (this.notYetPlayed) {
				this.notYetPlayed = false;
				this.bigPlayButton = false;
			}
		},
		checkForHDFile(url) {
			axios
				.head(url)
				.then((res) => {
					//console.log(res);
					if (res.status !== 200) {
						this.hdAvailable = false;
						this.hdActive = false;
					}
				})
				.catch((err) => {
					this.hdAvailable = false;
					this.hdActive = false;
				});
		},
	},
};
</script>

<style lang="scss">
.VideoPlayer {
	background-color: $color-black;
	display: inline-block;
	overflow: initial;
	position: relative;
	// max-width is only needed when disableFit = true
	max-width: 100%;
	.VideoPlayer-container {
		display: flex;
		flex-direction: column;
		// hide overflow so controls don't get wider than container in lightbox
		// not sure why this is needed
		overflow: hidden;
	}
	.VideoPlayer-video {
		flex: 0 0 auto;
		cursor: pointer;
		video {
			display: block;
			// max-width is only needed when disableFit = true
			max-width: 100%;
		}
	}
	.LoadingAnimation {
		background: transparent;
		display: flex;
		align-items: center;
		justify-items: center;
		pointer-events: none;
		.Icon {
			display: flex;
			fill: $color-neutral-2;
			stroke: $color-neutral-4;
			stroke-width: 1px;
			opacity: 0.7;
		}
	}
	// big play button
	.VideoPlayer-bigPlayButton {
		@include centerCenter;
		.Button {
			@include inset-md;
			@include dropShadow(3);
			.Icon {
				@include iconSize(2.5rem);
			}
		}
	}

	.MediaControls-controls {
		flex: 1 0 auto;
	}
	.MediaControls-seek {
		overflow: hidden;
	}
}

/* --------------------------------
	VARIANTS
-------------------------------- */

// White variant makes video bg white (used for map animations)
.VideoPlayer--white {
	background-color: $color-white;
}

// No controls variant used for clean screenshots
.VideoPlayer--noControls {
	.VideoPlayer-bigPlayButton {
		display: none;
	}
	.MediaControls-seek-container {
		display: none;
	}
	.MediaControls-controls {
		display: none;
	}
}

.VideoPlayer--640 {
	video {
		width: 640px;
		height: 360px;
	}
}

.VideoPlayer--1280 {
	video {
		width: 1280px;
		height: 720px;
	}
}
</style>
